import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

const authConfig = {
  authority: import.meta.env.VITE_AUTH_URL || 'https://auth.postosopcao.com.br',
  client_id: import.meta.env.VITE_CLIENT_ID || 'afericoes-admin',
  redirect_uri: `${window.location.origin}/callback`,
  response_type: 'code',
  scope: 'openid profile email offline_access roles afericoes-api',
  post_logout_redirect_uri: window.location.origin,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: true,
  silent_redirect_uri: `${window.location.origin}/silent-callback.html`,
};

const userManager = new UserManager(authConfig);

let accessToken = null;
let tokenExpiry = null;

userManager.getUser().then((user) => {
  if (user) {
    accessToken = user.access_token;
    tokenExpiry = user.expires_at ? user.expires_at * 1000 : null;
    
    // Salvar token no localStorage para uso no interceptor
    if (accessToken) {
      localStorage.setItem('auth_token', JSON.stringify({ access_token: accessToken }));
    }
    
    // Salvar dados do usuário
    if (user.profile) {
      let role = user.profile.role || user.profile['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || 'Manager';
      // Normalizar role: converter primeira letra para maiúscula se necessário
      // Mas manter roles específicas como 'afericoes.admin' como estão
      if (role && !role.includes('.')) {
        role = role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
      }
      localStorage.setItem('auth_user', JSON.stringify({
        sub: user.profile.sub,
        name: user.profile.name,
        email: user.profile.email,
        role: role,
      }));
    }
  }
});

export const signIn = async () => {
  try {
    await userManager.signinRedirect();
  } catch (error) {
    console.error('[OIDC] Erro ao fazer signIn:', error);
    throw error;
  }
};

// Função auxiliar para tentar remover cookies acessíveis (não HttpOnly)
const clearCookies = () => {
  try {
    // Obter domínio atual
    const domain = window.location.hostname;
    const domainParts = domain.split('.');
    
    // Tentar remover cookies comuns de autenticação
    const cookieNames = [
      '.AspNetCore.Identity.Application',
      'idsrv',
      'idsrv.session',
      'OpenIdConnect.nonce',
      'oidc.user',
      'auth',
      'authentication'
    ];
    
    // Tentar remover cookies do domínio atual e do domínio pai
    const domainsToTry = [domain];
    if (domainParts.length > 1) {
      // Adicionar domínio pai (ex: .example.com)
      domainsToTry.push('.' + domainParts.slice(-2).join('.'));
    }
    
    domainsToTry.forEach(dom => {
      cookieNames.forEach(cookieName => {
        // Tentar remover com diferentes paths
        const paths = ['/', '/connect', '/account'];
        paths.forEach(path => {
          // Remover cookie sem valor e com data de expiração no passado
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${dom};`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${dom}; SameSite=Lax;`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${dom}; SameSite=Strict;`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${dom}; Secure;`;
        });
      });
    });
    
    console.log('[OIDC] Tentativa de limpar cookies acessíveis concluída');
  } catch (error) {
    console.warn('[OIDC] Erro ao tentar limpar cookies:', error);
  }
};

export const signOut = async () => {
  try {
    console.log('[OIDC] Iniciando processo de logout...');
    
    // Obter usuário atual para id_token_hint
    const user = await userManager.getUser();
    console.log('[OIDC] Usuário obtido:', { 
      hasUser: !!user, 
      hasIdToken: !!user?.id_token,
      idToken: user?.id_token ? 'presente' : 'ausente'
    });
    
    // Limpar dados locais primeiro
    accessToken = null;
    tokenExpiry = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    
    // Tentar remover cookies acessíveis (não HttpOnly)
    // Nota: Cookies HttpOnly só podem ser removidos pelo servidor
    clearCookies();
    
    // Construir URL de logout diretamente
    // O servidor de autenticação implementou o logout em /connect/logout
    const authUrl = authConfig.authority || 'http://localhost:5000';
    
    // Construir parâmetros da URL
    const params = new URLSearchParams();
    if (user?.id_token) {
      params.append('id_token_hint', user.id_token);
    }
    params.append('post_logout_redirect_uri', window.location.origin);
    
    const logoutUrl = `${authUrl}/connect/logout?${params.toString()}`;
    
    console.log('[OIDC] Redirecionando para endpoint de logout:', {
      logoutUrl,
      hasIdTokenHint: !!user?.id_token,
      postLogoutRedirectUri: window.location.origin
    });
    
    // IMPORTANTE: O cookie HttpOnly só pode ser removido pelo servidor de autenticação
    // quando processar o logout em /connect/logout. O redirecionamento abaixo garante
    // que o servidor processe o logout e remova o cookie.
    
    // Tentar usar signoutRedirect primeiro (se o metadata estiver configurado)
    // Se falhar, usar redirecionamento manual direto
    try {
      await userManager.signoutRedirect({
        id_token_hint: user?.id_token,
        post_logout_redirect_uri: window.location.origin
      });
      console.log('[OIDC] signoutRedirect iniciado com sucesso');
    } catch (signoutError) {
      console.warn('[OIDC] signoutRedirect falhou (metadata pode não ter end_session_endpoint), usando redirecionamento manual:', signoutError.message);
      // Se o signoutRedirect falhar (por exemplo, se o metadata não tiver end_session_endpoint),
      // fazer redirecionamento manual direto para /connect/logout
      // O servidor processará o logout e removerá o cookie HttpOnly
      console.log('[OIDC] Redirecionando manualmente para:', logoutUrl);
      window.location.href = logoutUrl;
    }
  } catch (error) {
    console.error('[OIDC] Erro ao fazer logout:', error);
    console.error('[OIDC] Stack:', error.stack);
    
    // Mesmo com erro, limpar dados locais
    accessToken = null;
    tokenExpiry = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    
    // Tentar remover cookies acessíveis
    clearCookies();
    
    try {
      await userManager.removeUser();
    } catch (removeError) {
      console.error('[OIDC] Erro ao remover usuário:', removeError);
    }
    
    // Em caso de erro, sempre tentar redirecionar manualmente para o servidor processar o logout
    const authUrl = authConfig.authority || 'http://localhost:5000';
    const params = new URLSearchParams();
    params.append('post_logout_redirect_uri', window.location.origin);
    const logoutUrl = `${authUrl}/connect/logout?${params.toString()}`;
    console.log('[OIDC] Redirecionando manualmente (fallback):', logoutUrl);
    // O servidor processará o logout e removerá o cookie HttpOnly
    window.location.href = logoutUrl;
    
    throw error;
  }
};

let isProcessingCallback = false;

export const handleCallback = async () => {
  if (isProcessingCallback) {
    await new Promise(resolve => setTimeout(resolve, 500));
    const user = await userManager.getUser();
    if (user && !user.expired) {
      return user;
    }
    throw new Error('Callback já está sendo processado');
  }

  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  if (!code) {
    throw new Error('Nenhum código de autorização encontrado na URL');
  }

  isProcessingCallback = true;
  
  try {
    const user = await userManager.signinRedirectCallback();
    accessToken = user.access_token;
    tokenExpiry = user.expires_at ? user.expires_at * 1000 : null;
    
    // Salvar token
    if (accessToken) {
      localStorage.setItem('auth_token', JSON.stringify({ access_token: accessToken }));
    }
    
    // Salvar dados do usuário
    if (user.profile) {
      let role = user.profile.role || user.profile['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || 'Manager';
      // Normalizar role: converter primeira letra para maiúscula se necessário
      // Mas manter roles específicas como 'afericoes.admin' como estão
      if (role && !role.includes('.')) {
        role = role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
      }
      localStorage.setItem('auth_user', JSON.stringify({
        sub: user.profile.sub,
        name: user.profile.name,
        email: user.profile.email,
        role: role,
      }));
    }
    
    return user;
  } catch (error) {
    console.error('[OIDC] Erro ao processar callback:', error);
    isProcessingCallback = false;
    throw error;
  }
};

export const getAccessToken = async () => {
  if (window.location.pathname === '/callback') {
    const user = await userManager.getUser();
    if (user && !user.expired) {
      accessToken = user.access_token;
      tokenExpiry = user.expires_at ? user.expires_at * 1000 : null;
      return accessToken;
    }
    return null;
  }

  if (tokenExpiry && Date.now() >= tokenExpiry) {
    try {
      await refreshToken();
    } catch (error) {
      console.warn('[OIDC] Erro ao fazer refresh em getAccessToken:', error);
    }
  }

  if (accessToken) {
    return accessToken;
  }

  const user = await userManager.getUser();
  if (user && !user.expired) {
    accessToken = user.access_token;
    tokenExpiry = user.expires_at ? user.expires_at * 1000 : null;
    return accessToken;
  }

  return null;
};

export const refreshToken = async () => {
  const user = await userManager.getUser();
  if (user) {
    try {
      const refreshedUser = await userManager.signinSilent();
      accessToken = refreshedUser.access_token;
      tokenExpiry = refreshedUser.expires_at ? refreshedUser.expires_at * 1000 : null;
      
      if (accessToken) {
        localStorage.setItem('auth_token', JSON.stringify({ access_token: accessToken }));
      }
      
      return refreshedUser;
    } catch (error) {
      console.error('[OIDC] Erro ao fazer refresh do token:', error);
      await userManager.removeUser();
      accessToken = null;
      tokenExpiry = null;
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      throw error;
    }
  } else {
    throw new Error('Nenhum usuário encontrado para refresh');
  }
};

export const isAuthenticated = async () => {
  try {
    const user = await Promise.race([
      userManager.getUser(),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout ao obter usuário do UserManager')), 5000);
      })
    ]);
    
    return user !== null && !user.expired;
  } catch (error) {
    console.error('[OIDC] Erro ao verificar autenticação:', error);
    return false;
  }
};

export const getUser = async () => {
  return await userManager.getUser();
};

export const clearUser = async () => {
  accessToken = null;
  tokenExpiry = null;
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_user');
  await userManager.removeUser();
};

userManager.events.addUserLoaded((user) => {
  accessToken = user.access_token;
  tokenExpiry = user.expires_at ? user.expires_at * 1000 : null;
  if (accessToken) {
    localStorage.setItem('auth_token', JSON.stringify({ access_token: accessToken }));
  }
});

userManager.events.addUserUnloaded(() => {
  accessToken = null;
  tokenExpiry = null;
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_user');
});
